import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";

interface AppointmentRemindersConfigProps {
    useDefault: boolean;
    setUseDefault: (val: boolean) => void;
    reminderDays: number[];
    setReminderDays: (days: number[]) => void;
    reminderHours: number[];
    setReminderHours: (hours: number[]) => void;
}

export const AppointmentRemindersConfig: React.FC<AppointmentRemindersConfigProps> = ({
    useDefault,
    setUseDefault,
    reminderDays,
    setReminderDays,
    reminderHours,
    setReminderHours
}) => {
    const [dayInput, setDayInput] = useState("");
    // hourInput ya no es necesario
    const [error, setError] = useState<string>("");

    // --- MANEJO DE DÍAS ---
    const addDay = () => {
        const val = parseInt(dayInput.trim());
        if (isNaN(val) || val < 1) {
            if (val === 0) {
                setError("Para el mismo día, usa la sección de 'Horas antes' a la derecha.");
            } else {
                setError("Ingresa un número de días válido (>= 1)");
            }
            return;
        }
        if (reminderDays.includes(val)) {
            setError("Ese recordatorio ya existe");
            return;
        }
        setReminderDays([...reminderDays, val]);
        setDayInput("");
        setError("");
    };

    const removeDay = (val: number) => {
        setReminderDays(reminderDays.filter(d => d !== val));
    };

    // --- MANEJO DE HORAS (Optimizado para Single Selection) ---
    // La lógica de selección está inline en el botón para simplicidad visual.

    return (
        <Card className="relative overflow-hidden transition-all duration-300">
            <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
            <CardContent className="p-6">
                <div className="pl-4">
                    <div className="mb-4">
                        <Label className="text-lg font-semibold mb-0 flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-[#1CB3B3]" />
                            Configuración de Recordatorios
                        </Label>
                        <div className="text-xs text-gray-500 mt-1">Define cuándo enviar mensajes respecto a la fecha y hora de la cita.</div>
                    </div>

                    {/* Checkbox Default */}
                    <div className="flex items-start gap-3 mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
                        <input
                            type="checkbox"
                            id="useDefault"
                            checked={useDefault}
                            onChange={(e) => setUseDefault(e.target.checked)}
                            className="w-4 h-4 shrink-0 mt-0.5 text-[#1CB3B3] border-gray-300 rounded focus:ring-[#1CB3B3] cursor-pointer"
                        />
                        <label
                            htmlFor="useDefault"
                            className="min-w-0 text-sm font-medium leading-snug peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                            Usar recordatorios por defecto (24 horas antes y 1 hora antes)
                        </label>
                    </div>

                    {!useDefault && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in slide-in-from-top-4 duration-300">

                            {/* COLUMNA DÍAS */}
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm font-medium text-gray-700">Días antes de la cita</Label>
                                    <div className="flex flex-col sm:flex-row gap-2 mt-2">
                                        <input
                                            type="number"
                                            value={dayInput}
                                            onChange={(e) => setDayInput(e.target.value)}
                                            className="w-full min-w-0 sm:flex-1 h-10 px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm shadow-sm focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3] outline-none transition-all"
                                            placeholder="Ej: 1"
                                            onKeyDown={(e) => e.key === 'Enter' && addDay()}
                                        />
                                        <Button
                                            onClick={addDay}
                                            disabled={!dayInput}
                                            className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white hover:shadow-md transition-all h-10 px-4 rounded-xl"
                                        >
                                            Agregar
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Se enviará a la <strong>hora exacta</strong> de la cita, N días antes.
                                    </p>
                                </div>

                                {/* Chips Días */}
                                <div className="min-h-[60px] p-3 rounded-xl border border-gray-100 bg-gray-50/50">
                                    {reminderDays.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {[...reminderDays].sort((a, b) => b - a).map(d => (
                                                <div key={d} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg shadow-sm text-sm text-gray-700">
                                                    <span>{d} día(s) antes</span>
                                                    <button onClick={() => removeDay(d)} className="text-red-400 hover:text-red-600 transition-colors">✕</button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-gray-400 italic text-center py-2">Sin recordatorios de días</p>
                                    )}
                                </div>
                            </div>

                            {/* COLUMNA HORAS */}
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-sm font-medium text-gray-700">Horas antes (Mismo día)</Label>
                                    <p className="text-xs text-gray-400 mt-1 mb-3">
                                        Selecciona cuántas horas antes de la cita enviar el mensaje. Solo puedes elegir una opción.
                                    </p>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {[1, 2, 3, 4, 5, 6].map((h) => {
                                            const isSelected = reminderHours.includes(h);
                                            return (
                                                <Button
                                                    key={h}
                                                    variant={isSelected ? "default" : "outline"}
                                                    className={`
                                                        h-10 min-w-0 px-2 text-xs sm:text-sm transition-all rounded-xl relative overflow-hidden
                                                        ${isSelected
                                                            ? "bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white border-transparent shadow-md hover:shadow-lg hover:from-[#179999] hover:to-[#128080]"
                                                            : "bg-white text-gray-600 border-gray-200 hover:border-[#1CB3B3] hover:text-[#1CB3B3] hover:bg-[#1CB3B3]/5"
                                                        }
                                                    `}
                                                    onClick={() => {
                                                        // Toggle logic: if selected, deselect. If not, select ONLY this one.
                                                        if (isSelected) {
                                                            setReminderHours([]);
                                                        } else {
                                                            setReminderHours([h]);
                                                        }
                                                    }}
                                                >
                                                    {h} {h === 1 ? "Hora" : "Horas"}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[10px] text-gray-400 mt-2 text-center">
                                        Máximo 6 horas de anticipación para el mismo día.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mt-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 animate-in fade-in">
                            <AlertTriangle className="w-4 h-4" />
                            {error}
                        </div>
                    )}

                    {/* Ley 2300 Legal Disclaimer */}
                    <div className="mt-6 w-full min-w-0 p-3 sm:p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                        <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3">
                            <AlertTriangle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                            <div className="w-full min-w-0 sm:flex-1">
                                <p className="text-sm font-semibold text-cyan-900 mb-2 leading-snug break-words">
                                    Información importante - Ley 2300 de 2023
                                </p>
                                <p className="text-xs text-cyan-800 mb-2 leading-relaxed break-words">
                                    Debido a la <strong>Ley 2300 de 2023</strong> (Ley "Dejen de Fregar Colombia"),
                                    los horarios permitidos para el envío de mensajería o publicidad son:
                                </p>
                                <ul className="text-xs text-cyan-800 list-disc pl-4 mb-2 space-y-1 leading-relaxed">
                                    <li><strong>Lunes a viernes:</strong> 7:00 AM - 7:00 PM</li>
                                    <li><strong>Sábados:</strong> 8:00 AM - 3:00 PM</li>
                                    <li><strong>Domingos y festivos:</strong> Prohibido</li>
                                </ul>
                                <p className="text-xs text-cyan-700 italic leading-relaxed break-words">
                                    No nos hacemos responsables por ningún tipo de inconveniente, multa o sanción
                                    derivada del envío de mensajes fuera de los horarios permitidos por la ley.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
